import { useState } from 'react';
import { colors } from '../styles/colors';

interface MapLocationConsentModalProps {
  lang: string;
  onShow: (dontAskAgain: boolean) => void;
  onHide: (dontAskAgain: boolean) => void;
}

/**
 * Map visibility opt-in modal.
 *
 * Apple Guideline 5.1.2 requires that apps displaying user locations on a map:
 *  - ask for explicit permission before showing the user on the map,
 *  - allow the user to decline,
 *  - require manual check-in each time (no auto check-in).
 *
 * Shown on every login while the user is still in "hidden" state. If the user
 * checks "Don't ask again on this device", future logins will not re-prompt —
 * but the per-session hidden-by-default behavior is preserved (no auto check-in).
 * Users can always toggle visibility manually from Settings.
 */
export default function MapLocationConsentModal({ lang, onShow, onHide }: MapLocationConsentModalProps) {
  const isKo = lang === 'ko';
  const [dontAsk, setDontAsk] = useState(false);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-sm rounded-2xl p-6" style={{ backgroundColor: colors.surface, border: `1.5px solid ${colors.border}` }}>
        <div className="mb-3 text-center text-3xl">🗺️</div>
        <h3 className="mb-2 text-center text-base font-bold" style={{ color: colors.text }}>
          {isKo ? '지도에 내 위치 표시' : 'Show My Location on the Map'}
        </h3>
        <div className="mb-3 text-center text-xs leading-relaxed" style={{ color: colors.textMuted, wordBreak: 'keep-all' }}>
          {isKo ? (
            <>
              <p className="mb-2">현재 내 위치는 지도에 표시되지 않고 있습니다.</p>
              <p className="mb-1 text-left"><span className="font-semibold" style={{ color: colors.text }}>• 표시</span>: 나의 아바타가 지도와 "주변" 목록에 공개됩니다 (이번 접속에 한함).</p>
              <p className="mb-2 text-left"><span className="font-semibold" style={{ color: colors.text }}>• 숨김(기본값)</span>: 지도에 내 위치가 나타나지 않습니다. 무전 채팅과 주파수 기능은 평소대로 사용할 수 있습니다.</p>
              <p>설정에서 언제든지 수동으로 켜거나 끌 수 있으며, 로그아웃 시 자동으로 "숨김"으로 초기화됩니다.</p>
            </>
          ) : (
            <>
              <p className="mb-2">Your location is currently NOT shown on the map.</p>
              <p className="mb-1 text-left"><span className="font-semibold" style={{ color: colors.text }}>• Show</span>: your avatar appears on the map and the "Nearby" list (this session only).</p>
              <p className="mb-2 text-left"><span className="font-semibold" style={{ color: colors.text }}>• Keep Hidden (default)</span>: you do not appear on the map. Radio chat and frequency features still work.</p>
              <p>You can turn this on or off anytime from Settings. It automatically resets to "Hidden" on logout.</p>
            </>
          )}
        </div>
        <label className="mb-4 flex items-center gap-2 text-[11px] cursor-pointer"
          style={{ color: colors.textMuted }}>
          <input
            type="checkbox"
            checked={dontAsk}
            onChange={(e) => setDontAsk(e.target.checked)}
            className="h-3.5 w-3.5 cursor-pointer"
            style={{ accentColor: colors.primary }}
          />
          <span>
            {isKo
              ? '이 기기에서 다시 묻지 않기 (설정에서 변경 가능)'
              : "Don't ask again on this device (can be changed in Settings)"}
          </span>
        </label>
        <div className="flex gap-2">
          <button onClick={() => onHide(dontAsk)}
            className="flex-1 rounded-lg py-2.5 text-sm font-semibold"
            style={{ backgroundColor: colors.card, color: colors.textMuted, border: `1px solid ${colors.border}` }}>
            {isKo ? '숨기기' : 'Keep Hidden'}
          </button>
          <button onClick={() => onShow(dontAsk)}
            className="flex-1 rounded-lg py-2.5 text-sm font-bold text-white"
            style={{ backgroundColor: colors.primary }}>
            {isKo ? '지도에 표시' : 'Show on Map'}
          </button>
        </div>
      </div>
    </div>
  );
}
